import {
  GENERATOR_CODES,
  isGeneratorCode,
  type GeneratorCode,
} from "@csk/core";
import { certifyAllowedTransitionPath } from "@csk/demo-ui/domain";
import {
  shortestAllowedPath,
  withBridgeMatrixRules,
} from "../../bridgeMatrixIndex.js";
import type { Bridge, BridgeMatrix, MatrixRule } from "../../types.js";

export type EdgeToggleMap = Readonly<Partial<Record<string, boolean>>>;
export type GeneratorFootprint = Record<GeneratorCode, number>;

export interface WhatIfSimulationInput {
  readonly bridges: readonly Bridge[];
  readonly matrix: BridgeMatrix;
  readonly missingBridge: Bridge | null;
  readonly toggleMissing: boolean;
  readonly edgeToggles: EdgeToggleMap;
}

export interface WhatIfSimulation {
  readonly baseFootprint: GeneratorFootprint;
  readonly simulatedFootprint: GeneratorFootprint;
  readonly path: readonly string[] | null;
  readonly certification: ReturnType<typeof certifyAllowedTransitionPath> | null;
  readonly changedRules: number;
  readonly simulatedBridgeCount: number;
}

export function matrixRuleKey(rule: Pick<MatrixRule, "lhs" | "rhs">): string {
  return `${rule.lhs}::${rule.rhs}`;
}

export function bridgeFootprint(bridges: readonly Bridge[]): GeneratorFootprint {
  const counts: GeneratorFootprint = { G1: 0, G2: 0, G3: 0, G4: 0 };
  for (const bridge of bridges) {
    const generator = bridge.generator;
    if (isGeneratorCode(generator)) counts[generator] += 1;
  }
  return counts;
}

export function buildWhatIfSimulation({
  bridges,
  matrix,
  missingBridge,
  toggleMissing,
  edgeToggles,
}: WhatIfSimulationInput): WhatIfSimulation {
  const simulatedBridges =
    toggleMissing && missingBridge !== null
      ? bridges.some((bridge) => bridge.bridge_id === missingBridge.bridge_id)
        ? bridges
        : [...bridges, missingBridge]
      : bridges;

  const simulatedRules: MatrixRule[] = [];
  let changedRules = 0;
  for (const rule of matrix.rules) {
    if (!edgeToggles[matrixRuleKey(rule)]) {
      simulatedRules.push(rule);
      continue;
    }
    changedRules += 1;
    simulatedRules.push({
      ...rule,
      status: rule.status === "allowed" ? "forbidden" : "allowed",
    });
  }

  const simulatedMatrix = withBridgeMatrixRules(matrix, simulatedRules);
  const path = shortestAllowedPath(
    simulatedMatrix.index,
    "G2.CLASSIFY_INPUT",
    "G1.HTTP_GET",
  );

  return {
    baseFootprint: bridgeFootprint(bridges),
    simulatedFootprint: bridgeFootprint(simulatedBridges),
    path,
    certification:
      path === null ? null : certifyAllowedTransitionPath(simulatedMatrix.core, path),
    changedRules,
    simulatedBridgeCount: simulatedBridges.length,
  };
}

export function footprintRows(simulation: WhatIfSimulation): readonly (readonly string[])[] {
  return GENERATOR_CODES.map((axis) => [
    axis,
    String(simulation.baseFootprint[axis]),
    String(simulation.simulatedFootprint[axis]),
    String(simulation.simulatedFootprint[axis] - simulation.baseFootprint[axis]),
  ]);
}
